<template>
    <v-form @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header :title="formTitle"></card-header>
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="12">
                        <v-radio-group v-model="legal_person" row>
                            <v-card outlined class="mr-2">
                                <v-card-actions class="pa-5">
                                    <v-radio
                                        :label="
                                            $t(
                                                'prospects.form.labels.legal-person',
                                            )
                                        "
                                        :value="true"
                                    ></v-radio>
                                </v-card-actions>
                            </v-card>
                            <v-card outlined>
                                <v-card-actions class="pa-5">
                                    <v-radio
                                        :label="
                                            $t(
                                                'prospects.form.labels.normal-person',
                                            )
                                        "
                                        :value="false"
                                    ></v-radio>
                                </v-card-actions>
                            </v-card>
                        </v-radio-group>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            flat
                            v-model="model.trade_name"
                            :label="
                                legal_person
                                    ? $t('customer.form.labels.trade-name')
                                    : $t('customer.form.labels.name')
                            "
                            :counter="180"
                            :error-messages="tradeNameErrors"
                            @input="$v.model.trade_name.$touch()"
                            @blur="$v.model.trade_name.$touch()"
                            autofocus
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="model.legal_name"
                            :label="$t('customer.form.labels.legal_name')"
                            :counter="180"
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="!legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="model.cpf"
                            :label="$t('customer.form.labels.cpf')"
                            v-mask="'###.###.###-##'"
                            :counter="14"
                            :error-messages="cpfErrors"
                            @input="$v.model.cpf.$touch()"
                            @blur="$v.model.cpf.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="!legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="birthday"
                            :label="$t('prospects.form.labels.birthday')"
                            v-mask="'##/##/####'"
                            :counter="10"
                        ></v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="4"
                        v-show="legal_person"
                    >
                        <v-text-field
                            flat
                            v-model="model.cnpj"
                            :label="$t('customer.form.labels.cnpj')"
                            v-mask="'##.###.###/####-##'"
                            :counter="18"
                            :error-messages="cnpjErrors"
                            @input="$v.model.cnpj.$touch()"
                            @blur="$v.model.cnpj.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.email"
                            :label="$t('customer.form.labels.email')"
                            :error-messages="emailErrors"
                            @input="$v.model.email.$touch()"
                            @blur="$v.model.email.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.phone"
                            :label="$t('customer.form.labels.phone')"
                            v-mask="['(##) ####-####', '(##) #####-####']"
                            :error-messages="phoneErrors"
                            @input="$v.model.phone.$touch()"
                            @blur="$v.model.phone.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.responsible_name"
                            :label="$t('customer.form.labels.responsible_name')"
                            :counter="180"
                            :error-messages="responsibleNameErrors"
                            @input="$v.model.responsible_name.$touch()"
                            @blur="$v.model.responsible_name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.activity_area"
                            :label="$t('customer.form.labels.activity_area')"
                            :counter="180"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row v-if="!model.customer_id">
                    <v-col md="3">
                        <v-checkbox
                            v-model="model.create_customer"
                            :label="$t('prospects.index.buttons.create-trial')"
                            :value="true"
                        >
                        </v-checkbox>
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading"> </form-actions>
        </v-card>
    </v-form>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { mask } from 'vue-the-mask'
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import {
    required,
    minLength,
    maxLength,
    email,
    requiredIf,
} from 'vuelidate/lib/validators'

import BaseModel from '@/shared/orm/Model'

export default {
    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        isEdit: Boolean,
        titlePrefix: String,
    },

    data: () => {
        return {
            loading: false,
            legal_person: true,
            birthday: null,
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
    },

    directives: { mask },

    mixins: [validationMixin],

    validations: {
        model: {
            trade_name: { required, maxLength: maxLength(180) },
            cpf: {
                required: requiredIf(customer => {
                    return !customer.cnpj
                }),
                minLength: minLength(14),
            },
            cnpj: {
                required: requiredIf(customer => {
                    return !customer.cpf
                }),
                minLength: minLength(18),
            },
            email: { required, email },
            phone: {
                required,
                minLength: minLength(14),
            },
            responsible_name: { required, maxLength: maxLength(180) },
        },
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.messages.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()
            clone.birthday = this.birthday
                ? this.$moment(this.birthday, 'DD/MM/YYYY')
                : null

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('prospects.form.messages.success', {
                    lead: clone.trade_name,
                }),
            )

            this.$emit('saved')
        },
    },

    created() {
        if (this.isEdit && this.model.cpf) {
            this.legal_person = false
        }

        if (this.isEdit) {
            this.birthday = this.model.birthday
        }
    },

    computed: {
        formTitle() {
            return this.isEdit
                ? this.$t('prospects.form.title-edit')
                : this.$t('prospects.form.title-add')
        },

        tradeNameErrors() {
            const errors = []
            if (!this.$v.model.trade_name.$dirty) return errors

            !this.$v.model.trade_name.maxLength &&
                errors.push(
                    this.$t('customer.form.messages.name-error-max-length'),
                )

            !this.$v.model.trade_name.required &&
                errors.push(
                    this.$t('customer.form.messages.name-error-required'),
                )
            return errors
        },

        cpfErrors() {
            const errors = []
            if (!this.$v.model.cpf.$dirty) return errors

            !this.$v.model.cpf.required &&
                errors.push(
                    this.$t('customer.form.messages.cpf-error-required'),
                )

            !this.$v.model.cpf.minLength &&
                errors.push(
                    this.$t('customer.form.messages.cpf-error-min-length'),
                )

            return errors
        },

        cnpjErrors() {
            const errors = []
            if (!this.$v.model.cnpj.$dirty) return errors

            !this.$v.model.cnpj.required &&
                errors.push(
                    this.$t('customer.form.messages.cnpj-error-required'),
                )

            !this.$v.model.cnpj.minLength &&
                errors.push(
                    this.$t('customer.form.messages.cnpj-error-min-length'),
                )

            return errors
        },

        emailErrors() {
            const errors = []
            if (!this.$v.model.email.$dirty) return errors

            !this.$v.model.email.required &&
                errors.push(
                    this.$t('customer.form.messages.email-error-required'),
                )

            !this.$v.model.email.email &&
                errors.push(this.$t('customer.form.messages.email-error-email'))

            return errors
        },

        phoneErrors() {
            const errors = []
            if (!this.$v.model.phone.$dirty) return errors

            !this.$v.model.phone.required &&
                errors.push(
                    this.$t('customer.form.messages.phone-error-required'),
                )

            !this.$v.model.phone.minLength &&
                errors.push(
                    this.$t('customer.form.messages.phone-error-min-length'),
                )

            return errors
        },
        responsibleNameErrors() {
            const errors = []
            if (!this.$v.model.responsible_name.$dirty) return errors

            !this.$v.model.responsible_name.required &&
                errors.push(
                    this.$t(
                        'customer.form.messages.responsible-name-error-required',
                    ),
                )

            return errors
        },
    },
}
</script>
